import { FC, useCallback, useMemo } from 'react';
import intl from 'react-intl-universal';
import {
  Dropdown,
  FormItem,
  tailwindOverride,
  Typography,
} from '@getsynapse/design-system';
import { Option } from 'utils/customTypes';
import { useProjectStages } from 'Pages/ProjectsListPage/hooks/useProjectStages/useProjectStages';
import MultipleOptionListItem from 'Molecules/MultipleOptionsListItem/MultipleOptionsListItem';

interface ProjectStageFilterProps {
  onSelect: (selected: string[]) => void;
  selected?: string[];
  selectedProcesses?: string[];
  updateSelectedProcesses?: (selected: string[]) => void;
}

interface StageOption extends Option {
  processId: string;
  processName: string;
}

const ProjectStageFilter: FC<ProjectStageFilterProps> = ({
  onSelect,
  selected,
  selectedProcesses,
  updateSelectedProcesses,
}) => {
  const { stages } = useProjectStages({
    appliedFilters: [],
  });

  const options = useMemo(() => {
    return stages.map((stage) => ({
      label: stage.name,
      value: stage.id,
      processId: stage.processId,
      processName: stage.processName,
    }));
  }, [stages]);

  const selectedStages = useMemo(() => {
    return options.filter((option) => selected?.includes(option.value));
  }, [options, selected]);

  const handleChange = useCallback(
    (options: StageOption[]) => {
      let shouldUpdateProcesses = false;
      const selectedStages = [];
      const updatedProcesses = selectedProcesses || [];
      for (const stage of options) {
        selectedStages.push(stage.value);
        if (!updatedProcesses.includes(stage.processId)) {
          updatedProcesses.push(stage.processId);
          shouldUpdateProcesses = true;
        }
      }
      onSelect(selectedStages);
      if (shouldUpdateProcesses && updateSelectedProcesses) {
        updateSelectedProcesses(updatedProcesses);
      }
    },
    [selectedProcesses, onSelect, updateSelectedProcesses]
  );

  const renderCustomOption = useCallback(
    (
      option: StageOption,
      isSelected: boolean,
      selectOption: () => void,
      { className, ...otherProps }: { className: string }
    ) => {
      return (
        <li
          {...otherProps}
          key={option.value}
          className={tailwindOverride('group flex items-center', className, {
            'hover:bg-primary focus-visible:bg-primary': isSelected,
          })}
        >
          <MultipleOptionListItem
            label={option.label}
            isSelected={isSelected}
            selectOption={selectOption}
            value={option.value}
            className='truncate'
          />
          <Typography
            variant='label'
            className={tailwindOverride('text-primary-dark truncate ml-2', {
              'text-neutral-white': isSelected,
            })}
          >
            {`(${option.processName})`}
          </Typography>
        </li>
      );
    },
    []
  );

  return (
    <FormItem label={intl.get('PROJECT_DETAIL.STAGE')}>
      <Dropdown
        options={options}
        values={selectedStages}
        onChange={handleChange}
        multiple
        placeholder={intl.get('FILTER_GENERAL.PLACEHOLDER')}
        triggerProps={{
          'aria-label': 'project-stage-filter',
        }}
        renderOption={renderCustomOption}
      />
    </FormItem>
  );
};

export default ProjectStageFilter;
